<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Brand logo-->
      <b-link class="brand-logo login-brand-logo">
        <b-avatar
          src="/logo.png"
          size="64px"
          class="login-brand-avatar"
        />
      </b-link>
      <!-- /Brand logo-->

      <!-- Left brand panel -->
      <b-col
        lg="8"
        class="d-none d-lg-flex login-brand-panel p-0"
      >
        <div class="login-brand-inner">
          <div class="login-brand-mark">
            <div
              class="login-monogram"
              aria-hidden="true"
            >
              F
            </div>
            <div class="login-brand-title">
              Farm Business
              <small>Cooperative platform</small>
            </div>
          </div>

          <div class="login-eyebrow">
            Built for stronger organizations
          </div>

          <h2 class="login-hero">
            Manage your cooperative with
            <em>clarity and confidence.</em>
          </h2>

          <p class="login-lead">
            A secure, all-in-one cooperative and membership management platform
            for farmer organizations, associations, unions, community groups and
            outgrower schemes.
          </p>

          <div
            class="login-metrics"
            aria-label="Platform highlights"
          >
            <div class="login-metric">
              <strong>360°</strong>
              <span>Complete operational visibility</span>
            </div>
            <div class="login-metric">
              <strong>6</strong>
              <span>Core management modules</span>
            </div>
            <div class="login-metric">
              <strong>24/7</strong>
              <span>Secure digital access</span>
            </div>
          </div>

          <div class="login-company">
            <p class="login-company-copy">
              <strong>Developed by Orion Edge Limited.</strong>
              Digital solutions that improve efficiency, strengthen governance
              and help organizations deliver services at scale.
            </p>
            <div class="login-contact">
              farmbusiness@gmail.com<br>
              +233 534 216 581
            </div>
          </div>
        </div>
      </b-col>
      <!-- /Left brand panel -->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <div class="login-mobile-brand d-lg-none mb-2">
            <div
              class="login-monogram login-monogram--light"
              aria-hidden="true"
            >
              F
            </div>
            <span>Farm Business</span>
          </div>

          <p class="login-card-kicker mb-50">
            Secure account access
          </p>
          <b-card-title
            title-tag="h2"
            class="font-weight-bold mb-1"
          >
            Welcome back
          </b-card-title>
          <b-card-text class="mb-2">
            Enter your details to continue to your cooperative platform.
          </b-card-text>

          <!-- form -->
          <validation-observer
            ref="loginValidation"
            #default="{invalid}"
          >
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login()"
            >
              <!-- email -->
              <b-form-group
                label="Email"
                label-for="login-email"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-input
                    id="login-email"
                    v-model="credentials.email"
                    :state="errors.length > 0 ? false:null"
                    name="login-email"
                    placeholder="Enter your email"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- forgot password -->
              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="login-password">Password</label>
                  <b-link :to="{name:'reset'}">
                    <small>Forgot Password?</small>
                  </b-link>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="credentials.password"
                      :state="errors.length > 0 ? false:null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="············"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- checkbox -->
              <b-form-group>
                <b-form-checkbox
                  id="remember-me"
                  v-model="credentials.remember_me"
                  name="checkbox-1"
                >
                  Remember Me
                </b-form-checkbox>
              </b-form-group>

              <!-- submit buttons -->
              <div class="float-right">
                <am-button
                  label="Sign In"
                  :disabled="invalid"
                  :loading="loggingIn"
                  @click="login()"
                />
              </div>
            </b-form>
          </validation-observer>

          <p class="login-powered text-muted text-center mt-3 mb-0">
            Powered by Orion Edge Limited
          </p>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BLink, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BFormCheckbox, BCardText, BCardTitle, BForm, BAvatar,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import AmButton from '@/farmbusiness/components/AmButton'
import showToast from '@/farmbusiness/mixins/showToast'

export default {
  components: {
    AmButton,
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BForm,
    ValidationProvider,
    ValidationObserver,
    BAvatar,
  },
  mixins: [togglePasswordVisibility, showToast],
  data() {
    return {
      credentials: {
        email: null,
        password: null,
        remember_me: false,
      },
      required,
      email,
      loggingIn: false,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    login() {
      this.loggingIn = true
      this.$http.post('auth/login', this.credentials).then(res => {
        this.$store.commit('auth/setAuthState', res.data)

        if (res.data.is_super_user) {
          this.showSuccess('Login successful')
          this.$router.push({ name: 'choose-company' })
        } else {
          this.$store.commit('company/setCompany', res.data.company)
          this.$store.commit('company/setCurrency', res.data.currency)
          this.showSuccess('Login successful')
          this.$router.push('/')
        }
      }).catch(err => {
        if (err.response && err.response.status === 401) {
          this.showError(err.response.data || 'Company is inactive. Please contact your administrator.')
        } else {
          this.showError('Failed to login, please try again')
        }
      }).finally(() => {
        this.loggingIn = false
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
@import '~@/assets/scss/variables/_variables.scss';

.login-brand-logo {
  .login-brand-avatar {
    background-color: #fff !important;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }

  .b-avatar-img img,
  .login-brand-avatar img {
    object-fit: contain;
    padding: 8px;
  }
}

.login-brand-panel {
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  color: #fff;
  background:
    radial-gradient(circle at 12% 18%, rgba(255, 255, 255, 0.16), transparent 34%),
    radial-gradient(circle at 88% 82%, rgba(0, 0, 0, 0.18), transparent 42%),
    linear-gradient(145deg, darken($primary, 18%) 0%, $primary 48%, darken($primary, 8%) 100%);
  overflow: hidden;
  padding: clamp(38px, 5vw, 74px) !important;
  align-items: center;
}

.login-brand-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 42px 42px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.55), transparent 85%);
  pointer-events: none;
  z-index: 0;
}

.login-brand-inner {
  position: relative;
  z-index: 1;
  display: flex;
  width: min(100%, 800px);
  margin: 0 auto;
  min-height: 0;
  flex-direction: column;
  justify-content: center;
}

.login-brand-mark {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: clamp(34px, 5.5vh, 64px);
}

.login-monogram {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.2);
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.02em;
}

.login-monogram--light {
  border-color: rgba($primary, 0.28);
  background: rgba($primary, 0.1);
  color: $primary;
}

.login-brand-title {
  font-weight: 800;
  font-size: 16px;
  letter-spacing: -0.01em;
  line-height: 1.2;

  small {
    display: block;
    margin-top: 3px;
    color: rgba(255, 255, 255, 0.78);
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }
}

.login-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 17px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.17em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 32px;
    height: 2px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.75);
  }
}

.login-hero {
  max-width: 720px;
  margin: 0;
  font-weight: 700;
  font-size: clamp(34px, 4.2vw, 58px);
  line-height: 1.06;
  letter-spacing: -0.04em;
  color: #fff;

  em {
    color: rgba(255, 255, 255, 0.86);
    font-style: normal;
  }
}

.login-lead {
  max-width: 660px;
  margin-top: 22px;
  margin-bottom: 0;
  color: rgba(255, 255, 255, 0.84);
  font-size: clamp(15px, 1.2vw, 18px);
  line-height: 1.68;
}

.login-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: clamp(28px, 4.6vh, 46px);
}

.login-metric {
  padding: 17px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);

  strong {
    display: block;
    color: #fff;
    font-weight: 800;
    font-size: 20px;
  }

  span {
    display: block;
    margin-top: 7px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
  }
}

.login-company {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-top: clamp(28px, 4.8vh, 50px);
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.login-company-copy {
  max-width: 530px;
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
  font-size: 13px;
  line-height: 1.62;

  strong {
    color: #fff;
  }
}

.login-contact {
  flex: none;
  color: rgba(255, 255, 255, 0.86);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.72;
  text-align: right;
}

.login-mobile-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4b4b4b;
  font-weight: 800;
  font-size: 14px;
}

.login-card-kicker {
  display: flex;
  align-items: center;
  gap: 9px;
  color: $primary;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 21px;
    height: 2px;
    border-radius: 2px;
    background: rgba($primary, 0.55);
  }
}

.login-powered {
  clear: both;
  padding-top: 1.5rem;
  font-size: 12px;
}

@media (min-width: 992px) and (max-height: 760px) {
  .login-brand-panel {
    padding: 30px 46px !important;
  }

  .login-brand-mark {
    margin-bottom: 26px;
  }

  .login-hero {
    font-size: clamp(32px, 3.6vw, 48px);
  }

  .login-lead {
    margin-top: 15px;
    line-height: 1.52;
  }

  .login-metrics {
    margin-top: 22px;
  }

  .login-metric {
    padding: 12px 14px;
  }

  .login-company {
    margin-top: 22px;
    padding-top: 13px;
  }
}

@media (max-width: 1199px) {
  .login-metrics {
    grid-template-columns: 1fr;
  }
}
</style>
